import dayjs from 'dayjs'
import { toast } from 'sonner'

export const isSuccess = (res: any) => {
  return (
    (res?.status >= 200 && res?.status < 300) ||
    (res?.statusCode >= 200 && res?.statusCode < 300) ||
    res?.data?.statusCode === 200
  )
}

export const simplifyBackendError = (error: any) => {
  if (typeof error === 'string') {
    return error
  } else if (Array.isArray(error)) {
    return error.join(', ')
  } else {
    return 'An error occurred'
  }
}

const toastConfig = {
  duration: 3000,
}

const baseStyle = {
  background: '#ffffff',
  color: '#111827',
  border: '1px solid #e5e7eb',
  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  borderRadius: '8px',
  width: 'auto',
  marginLeft: '0px',
}

type ToastType = 'info' | 'success' | 'warning' | 'error'

export const notify = (msg: string, type: ToastType) => {
  if (type === 'info') {
    return toast.info(msg, {
      ...toastConfig,
      style: {
        ...baseStyle,
        borderLeft: '4px solid #3b82f6',
        width: '100%',
      },
      className: 'custom-info-icon',
    })
  } else if (type === 'success') {
    return toast.success(msg, {
      ...toastConfig,
      style: {
        ...baseStyle,
        whiteSpace: 'nowrap',
        width: '100%',
        borderLeft: '4px solid #22c55e',
      },
      className: 'custom-success-icon',
    })
  } else if (type === 'warning') {
    return toast.warning(msg, {
      ...toastConfig,
      style: {
        ...baseStyle,
        width: '100%',
        borderLeft: '4px solid #f59e0b',
      },
      className: 'custom-warning-icon',
    })
  } else if (type === 'error') {
    if (
      msg?.toLowerCase()?.trim() === 'forbidden resource' ||
      msg?.toLowerCase()?.trim() === 'insufficient permissions' ||
      !msg
    ) {
      return
    }
    return toast.error(msg || 'An error occurred', {
      ...toastConfig,
      style: {
        ...baseStyle,
        width: '100%',
        borderLeft: '4px solid #ef4444',
      },
      className: 'custom-error-icon',
    })
  }
}

export const dayjsFormat = (date: any, format: string) => (date ? dayjs(date).format(format) : '')

// Formats an integer (or fractional) amount of cents into a "$X.XX" string.
export const formatCurrency = (cents?: number): string => {
  const value = (cents ?? 0) / 100
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// 1234 -> "1,234". A missing value reads as 0.
export const formatCount = (n?: number): string => (n ?? 0).toLocaleString('en-US')

// 1500 -> "1.5K", 2400000 -> "2.4M"; below 1000 the number as it is.
export const abbreviate = (n: number): string => {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return `${n}`
}

export const camelToSentenceCase = (str: string): string => {
  if (!str) return ''
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
    .trim()
}

export const formatPlanName = (str: string): string => {
  if (!str) return 'N/A'
  const spaced = str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/plus/gi, ' Plus')
    .replace(/pro/gi, 'Pro ')
    .replace(/\s+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export const getInitials = (name?: string): string => {
  if (!name) return '?'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const formatDate = (dateToFormat: string | number): string => {
  if (!dateToFormat) return ''
  const date = new Date(dateToFormat)
  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

export const formatTimestamp = (dateToFormat: number): string => {
  if (!dateToFormat) return ''
  const date = new Date(dateToFormat * 1000)
  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

export const formatPhoneNumber = (input: string, prevInput: string) => {
  if (input?.length < prevInput?.length) return input
  // Strip all non-numeric characters from the input
  const strippedInput = input?.replace(/\D/g, '')

  // Check if input contains country code (more than 10 digits)
  if (strippedInput?.length > 10) {
    // Extract country code and phone number
    const countryCodeLength = strippedInput.length - 10
    const countryCode = strippedInput.substring(0, countryCodeLength)
    const phoneNumber = strippedInput.substring(countryCodeLength)

    // Format the 10-digit phone number
    const match = phoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

    let formattedPhone = ''
    if (match) {
      if (phoneNumber.length >= 6) formattedPhone = `(${match[1]}) ${match[2]}-${match[3]}`
      else if (phoneNumber.length >= 3) formattedPhone = `(${match[1]}) ${match[2]}${match[3]}`
      else formattedPhone = `${match[1]}${match[2]}${match[3]}`
    }

    return countryCode === '1' ? formattedPhone : `+${countryCode} ${formattedPhone}`
  }

  // Use regex to split the input into groups of three digits and a group of up to four digits
  const match = strippedInput?.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

  // Reformat the input with parentheses around the first three digits and a hyphen between the second and third groups
  let formattedInput = ''
  if (match) {
    if (strippedInput.length >= 6) formattedInput = `(${match[1]}) ${match[2]}-${match[3]}`
    else if (strippedInput.length >= 3) formattedInput = `(${match[1]}) ${match[2]}${match[3]}`
    else formattedInput = `${match[1]}${match[2]}${match[3]}`
  } else {
    formattedInput = strippedInput
  }
  return formattedInput
}
